import { useCallback, useEffect, useRef, useState } from 'react'
import { Socket, Channel } from 'phoenix'
import { BASE_URL, JWT_TOKEN, ALL_ORGS_SELECTED } from 'lib/constants'
import {
  BluetoothScanObservation,
  DiscoveredDeviceType,
} from 'lib/types/bluetoothPairing'
import { aggregateReadings } from './aggregateReadings'

export type ScanStatus = 'idle' | 'connecting' | 'connected' | 'lost' | 'failed' | 'disconnected'

export interface UseBluetoothScanStreamResult {
  status: ScanStatus
  error?: string
  message?: string
  discoveries: DiscoveredDeviceType[]
  connect: () => void
  disconnect: () => void
  select: (names: string[]) => void
  clear: () => void
}

const NO_READINGS_WATCHDOG_MS = 30000
// Portal pings the Goldline on this cadence so it doesn't time out and leave
// pairing mode. No pong is expected back.
const PING_INTERVAL_MS = 5000

const PAIRING_MODE_MESSAGE = 'HC goldline to enter pairing mode on device'

const socketUrl = () => {
  const base = (BASE_URL || '').replace(/^http/, 'ws').replace(/\/$/, '')
  return `${base}/socket`
}

const coerceObservation = (
  raw: any,
  fallbackScannerId: string,
  seenAt: string,
  index: number,
): BluetoothScanObservation | null => {
  // The scanner may send a plain device-name string instead of an object.
  // Synthesize a unique mac per entry so each one counts as a distinct device
  // (the aggregation tallies unique macs per name).
  if (typeof raw === 'string') {
    const name = raw.trim()
    if (!name) return null
    return {
      mac: `${fallbackScannerId}:${name}:${index}`.toLowerCase(),
      name,
      rssi: -100,
      scannerId: fallbackScannerId,
      seenAt,
    }
  }

  if (!raw || typeof raw !== 'object') return null
  const mac: string | undefined =
    raw.mac || raw.address || raw.bd_addr || (typeof raw.id === 'string' ? raw.id : undefined)
  if (!mac) return null
  const rssi = typeof raw.rssi === 'number' ? raw.rssi : -100
  // The whitelist is keyed on device name now; fall back to the MAC so an
  // unnamed reading still surfaces rather than disappearing.
  const name = (typeof raw.name === 'string' && raw.name) || raw.advertisedName || mac
  return {
    mac: mac.toLowerCase(),
    name,
    rssi,
    scannerId: raw.scanner_id || raw.scannerId || raw.alert_source_id || fallbackScannerId,
    seenAt: raw.seen_at || raw.seenAt || seenAt,
  }
}

export function useBluetoothScanStream(orgId: string | undefined): UseBluetoothScanStreamResult {
  const [status, setStatus] = useState<ScanStatus>('idle')
  const [error, setError] = useState<string | undefined>(undefined)
  const [message, setMessage] = useState<string | undefined>(undefined)
  const [discoveries, setDiscoveries] = useState<DiscoveredDeviceType[]>([])

  const socketRef = useRef<Socket | null>(null)
  const channelRef = useRef<Channel | null>(null)
  const readingsByScannerRef = useRef<Map<string, BluetoothScanObservation[]>>(new Map())
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // The Goldline we're targeting, learned from the presence_diff that announced
  // its join. Sent with headset:start and headset:ping so the backend can route
  // them to that scanner.
  const targetAlertSourceIdRef = useRef<string | null>(null)
  const wantScanRef = useRef(false)
  const orgIdRef = useRef(orgId)

  orgIdRef.current = orgId

  const clearWatchdog = () => {
    if (watchdogTimerRef.current) {
      clearTimeout(watchdogTimerRef.current)
      watchdogTimerRef.current = null
    }
  }

  const armWatchdog = () => {
    clearWatchdog()
    watchdogTimerRef.current = setTimeout(() => {
      setStatus('lost')
      setError('No readings received')
    }, NO_READINGS_WATCHDOG_MS)
  }

  const clearPing = () => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current)
      pingTimerRef.current = null
    }
  }

  // Start pinging only once we have a Goldline to target. Idempotent: repeated
  // presence joins won't reset the cadence; each tick re-reads the latest target.
  const startPing = () => {
    if (pingTimerRef.current) return
    pingTimerRef.current = setInterval(() => {
      const target = targetAlertSourceIdRef.current
      if (!target) return
      // Fire-and-forget: the Goldline only needs to hear from us; no pong.
      channelRef.current?.push('headset:ping', { alert_source_id: target })
    }, PING_INTERVAL_MS)
  }

  const recomputeDiscoveries = () => {
    setDiscoveries(aggregateReadings(readingsByScannerRef.current))
  }

  const handleReadings = (payload: any) => {
    const now = new Date().toISOString()
    const rawDevices: any[] = Array.isArray(payload?.devices) ? payload.devices : []
    const scannerId =
      payload?.scanner_id || payload?.scannerId || payload?.alert_source_id || 'unknown-scanner'
    const observations: BluetoothScanObservation[] = rawDevices
      .map((d, i) => coerceObservation(d, scannerId, now, i))
      .filter((o): o is BluetoothScanObservation => o !== null)
    readingsByScannerRef.current.set(scannerId, observations)
    recomputeDiscoveries()
    setStatus('connected')
    setError(undefined)
    armWatchdog()
  }

  // Tell the targeted scanner to start streaming readings. Triggered by a
  // presence_diff join (the Goldline no longer sends a headset:entry command).
  const emitStart = (alertSourceId?: string | null) => {
    const target = alertSourceId ?? targetAlertSourceIdRef.current
    channelRef.current?.push('headset:start', target ? { alert_source_id: target } : {})
  }

  const teardown = () => {
    clearWatchdog()
    clearPing()
    targetAlertSourceIdRef.current = null
    if (channelRef.current) {
      try {
        channelRef.current.leave()
      } catch (_e) {
        /* ignore */
      }
      channelRef.current = null
    }
    if (socketRef.current) {
      try {
        socketRef.current.disconnect()
      } catch (_e) {
        /* ignore */
      }
      socketRef.current = null
    }
    readingsByScannerRef.current.clear()
  }

  const connect = useCallback(() => {
    wantScanRef.current = true
    const currentOrgId = orgIdRef.current
    if (!currentOrgId || currentOrgId === ALL_ORGS_SELECTED) return

    teardown()
    const token = localStorage.getItem(JWT_TOKEN) || ''
    const url = socketUrl()
    if (!url) {
      setStatus('failed')
      setError('API URL not configured')
      return
    }

    setStatus('connecting')
    setError(undefined)
    setMessage(undefined)

    const socket = new Socket(url, { params: { token } })
    socketRef.current = socket
    socket.onError(() => setError('Socket error'))
    socket.connect()

    // alert_source_id is required by the backend even for portal observers;
    // we send a stable placeholder until a real per-portal source id is wired in.
    const channel = socket.channel(`org:${currentOrgId}`, {
      portal: true,
      alert_source_id: 'portal-observer',
    })
    channelRef.current = channel

    channel.on('headset:readings', handleReadings)
    // Goldlines no longer emit headset:entry. When a member joins the channel,
    // Phoenix broadcasts a presence_diff (including the portal's own join) — that
    // is our cue to emit headset:start so the scanners begin streaming readings.
    channel.on('presence_diff', (payload: any) => {
      const joins = payload?.joins && typeof payload.joins === 'object' ? Object.values(payload.joins) : []
      if (joins.length === 0) return

      // Pull the joining scanner's alert_source_id out of the presence metas,
      // ignoring the portal's own presence (portal: true / portal-observer).
      let joinedTarget: string | null = null
      for (const entry of joins as any[]) {
        const metas = Array.isArray(entry?.metas) ? entry.metas : []
        const scanner = metas.find((m: any) => !m?.portal && m?.alert_source_id)
        if (scanner) {
          joinedTarget = String(scanner.alert_source_id)
          break
        }
      }
      console.log('[bluetooth-pairing] presence_diff joins:', joins.length, 'target:', joinedTarget)

      // Only act once a real scanner (Goldline) has joined: tell it to start,
      // and begin the keepalive ping targeted at it.
      if (joinedTarget) {
        targetAlertSourceIdRef.current = joinedTarget
        emitStart(joinedTarget)
        startPing()
      }
    })
    channel.on('headset:start', payload => {
      console.log('[bluetooth-pairing] headset:start', payload)
    })
    channel.on('headset:selection', payload => {
      console.log('[bluetooth-pairing] headset:selection', payload)
    })

    channel
      .join()
      .receive('ok', () => {
        setStatus('connected')
        // Prompt the operator to put the Goldline into pairing mode; once it
        // joins the channel the presence_diff handler will emit headset:start.
        setMessage(PAIRING_MODE_MESSAGE)
        // Pinging starts from the presence_diff handler, once a Goldline joins.
        armWatchdog()
      })
      .receive('error', (reason: any) => {
        setStatus('failed')
        setError(typeof reason === 'string' ? reason : reason?.reason || 'Failed to join channel')
      })
      .receive('timeout', () => {
        setStatus('failed')
        setError('Channel join timed out')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const disconnect = useCallback(() => {
    wantScanRef.current = false
    // Tell the targeted Goldline to leave pairing mode before we drop the
    // socket. Read the target before teardown() clears it.
    const target = targetAlertSourceIdRef.current
    channelRef.current?.push('headset:stop', target ? { alert_source_id: target } : {})
    teardown()
    setMessage(undefined)
    setStatus('disconnected')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const select = useCallback((names: string[]) => {
    // Target the same Goldline as start/ping/stop.
    const target = targetAlertSourceIdRef.current
    channelRef.current?.push('headset:selection', target ? { names, alert_source_id: target } : { names })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clear = useCallback(() => {
    readingsByScannerRef.current.clear()
    setDiscoveries([])
  }, [])

  useEffect(() => {
    return () => {
      wantScanRef.current = false
      teardown()
      setDiscoveries([])
      setStatus('idle')
      setError(undefined)
      setMessage(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  return { status, error, message, discoveries, connect, disconnect, select, clear }
}
